<template>
    <div v-if="item" class="listener-artist">
        <div class="listener-artist__backdrop" :style="backdropStyle"></div>
        <div class="listener-artist__glow"></div>

        <div class="listener-artist__shell">
            <div class="listener-artist__topbar">
                <button class="listener-artist__icon-button" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="listener-artist__heading">
                    <div class="listener-artist__eyebrow">{{ $tt('艺术家详情') }}</div>
                    <div class="listener-artist__caption">{{ $tt('artist.details.stats.summary', { albumCount: item.albumCount, trackCount: item.trackCount }) }}</div>
                </div>
                <button class="listener-artist__icon-button" @click="addFavouriteArtist">
                    <svg viewBox="0 0 24 24" :fill="isFavourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                        <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                    </svg>
                </button>
            </div>

            <div class="listener-artist__hero-switch-wrap">
                <div class="listener-artist__hero-switch">
                    <button
                        :class="['listener-artist__hero-pill', !showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(false)"
                    >
                        {{ $tt('封面') }}
                    </button>
                    <button
                        :class="['listener-artist__hero-pill', showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(true)"
                    >
                        {{ $tt('概览') }}
                    </button>
                </div>
            </div>

            <section class="listener-artist__hero">
                <div class="listener-artist__display-stage">
                    <div v-if="!showOverviewPanel" class="listener-artist__art-stage">
                        <div class="listener-artist__vinyl"></div>
                        <div class="listener-artist__art-card">
                            <img
                                :src="item.image || '/static/dist/img/album_null.jpg'"
                                :alt="item.name"
                                class="listener-artist__art-image"
                            >
                        </div>
                    </div>
                    <div v-else class="listener-artist__overview-stage">
                        <div class="listener-artist__overview-card">
                            <div class="listener-artist__overview-label">{{ $tt('简介') }}</div>
                            <p class="listener-artist__overview-text">
                                {{ item.description || $tt('还没有补充简介，但你已经可以直接开始播放。') }}
                            </p>
                        </div>

                        <div class="listener-artist__overview-card">
                            <div class="listener-artist__overview-label">{{ $tt('艺术家信息') }}</div>
                            <div class="listener-artist__info-list">
                                <div class="listener-artist__info-row">
                                    <span>{{ $tt('专辑') }}</span>
                                    <strong>{{ item.albumCount || 0 }}</strong>
                                </div>
                                <div class="listener-artist__info-row">
                                    <span>{{ $tt('歌曲') }}</span>
                                    <strong>{{ item.trackCount || 0 }}</strong>
                                </div>
                                <div class="listener-artist__info-row">
                                    <span>{{ $tt('风格') }}</span>
                                    <strong>{{ genreSummary }}</strong>
                                </div>
                                <div class="listener-artist__info-row">
                                    <span>{{ $tt('相关') }}</span>
                                    <strong>{{ item.similarArtist ? item.similarArtist.length : 0 }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="listener-artist__meta">
                    <div class="listener-artist__meta-kicker">{{ $tt('艺术家') }}</div>
                    <h1 class="listener-artist__title">{{ item.name }}</h1>
                    <p
                        class="listener-artist__summary"
                        :class="item.description ? 'is-clickable' : ''"
                        @click="openArtistModal"
                    >
                        {{ item.description ? artistDescriptionPreview : $tt('还没有补充简介，但你已经可以直接开始播放。') }}
                    </p>

                    <div v-if="item.genres && item.genres.length" class="listener-artist__chips">
                        <router-link
                            v-for="genre in item.genres"
                            :key="genre"
                            :to="{ name: 'genre', params: { id: genre } }"
                            class="listener-artist__chip"
                        >
                            {{ genre }}
                        </router-link>
                    </div>

                    <div class="listener-artist__actions">
                        <button class="listener-artist__play-button" @click="playNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                            </svg>
                            <span>{{ $tt('播放热门') }}</span>
                        </button>
                        <button class="listener-artist__action-button" @click="shuffleNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                            </svg>
                            <span>{{ $tt('随机') }}</span>
                        </button>
                        <button class="listener-artist__action-button" @click="viewAll(item)">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h10v2H4z"></path>
                            </svg>
                            <span>{{ $tt('全部歌曲') }}</span>
                        </button>
                    </div>
                </div>
            </section>

            <div class="listener-artist__display-switch">
                <button
                    :class="['listener-artist__display-pill', activeSection === 'tracks' ? 'is-active' : '']"
                    @click="activeSection = 'tracks'"
                >
                    {{ $tt('歌曲') }}
                </button>
                <button
                    :class="['listener-artist__display-pill', activeSection === 'albums' ? 'is-active' : '']"
                    @click="activeSection = 'albums'"
                >
                    {{ $tt('专辑') }}
                </button>
                <button
                    :class="['listener-artist__display-pill', activeSection === 'similar' ? 'is-active' : '']"
                    @click="activeSection = 'similar'"
                >
                    {{ $tt('相关') }}
                </button>
            </div>

            <section v-if="activeSection === 'tracks'" class="listener-artist__tracks">
                <div class="listener-artist__panel-header">
                    <div>
                        <div class="listener-artist__panel-eyebrow">{{ $tt('热门歌曲') }}</div>
                        <h2 class="listener-artist__panel-title">{{ $tt('{count} 首歌曲', { count: item.topTracks.length }) }}</h2>
                    </div>
                </div>
                <ListenerTrackList :tracks="item.topTracks.slice(0, 8)" no-artist />
                <button v-if="item.topTracks.length > 5" class="listener-artist__more-button" @click="viewAll(item)">
                    {{ $tt('查看全部') }}
                </button>
            </section>

            <section v-else-if="activeSection === 'albums'" class="listener-artist__albums">
                <div class="listener-artist__panel-header">
                    <div>
                        <div class="listener-artist__panel-eyebrow">{{ $tt('最新专辑') }}</div>
                        <h2 class="listener-artist__panel-title">{{ $tt('{count} 张专辑', { count: item.albums.length }) }}</h2>
                    </div>
                </div>
                <ListenerAlbumList v-if="item.albums.length" :items="item.albums.slice(0, 12)" small />
                <div v-else class="listener-artist__empty-inline">{{ $tt('暂无专辑') }}</div>
            </section>

            <section v-else class="listener-artist__similar">
                <div class="listener-artist__panel-header">
                    <div>
                        <div class="listener-artist__panel-eyebrow">{{ $tt('相似艺术家') }}</div>
                        <h2 class="listener-artist__panel-title">{{ $tt('{count} 位艺术家', { count: item.similarArtist.length }) }}</h2>
                    </div>
                </div>
                <div v-if="item.similarArtist.length" class="listener-artist__similar-list">
                    <button
                        v-for="artist in item.similarArtist"
                        :key="artist.id"
                        class="listener-artist__similar-row"
                        @click="openArtist(artist)"
                    >
                        <img :src="artist.image || '/static/dist/img/album_null.jpg'" :alt="artist.name" class="listener-artist__similar-image">
                        <div class="listener-artist__similar-meta">
                            <div class="listener-artist__similar-name">{{ artist.name }}</div>
                            <div class="listener-artist__similar-sub">{{ $tt('{count} 张专辑', { count: artist.albumCount || 0 }) }}</div>
                        </div>
                        <svg class="listener-artist__similar-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M9 6l6 6-6 6"></path>
                        </svg>
                    </button>
                </div>
                <div v-else class="listener-artist__empty-inline">{{ $tt('暂无相似艺术家') }}</div>
            </section>
        </div>

        <dialog ref="artistModal" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('艺术家简介:') }}{{ item.name }}</h3>
                <div class="py-4 whitespace-pre-wrap break-words text-base-content/80">
                    {{ item.description || $tt('暂无简介') }}
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>

<script>
import ListenerTrackList from '@/views/library/track/ListenerTrackList.vue'
import ListenerAlbumList from '@/views/library/album/ListenerAlbumList.vue'

export default {
    name: 'ListenerArtistDetails',
    components: {
        ListenerTrackList,
        ListenerAlbumList
    },
    data() {
        return {
            item: null,
            id: this.$route.params.id,
            isFavourite: false,
            showOverviewPanel: false,
            activeSection: 'tracks'
        }
    },
    computed: {
        backdropStyle() {
            return {
                backgroundImage: `url('${this.item?.image || '/static/dist/img/album_null.jpg'}')`
            }
        },
        artistDescriptionPreview() {
            if (!this.item?.description) return ''
            return this.item.description.length > 140 ? `${this.item.description.slice(0, 140)}...` : this.item.description
        },
        genreSummary() {
            if (!this.item?.genres || !this.item.genres.length) {
                return '--'
            }
            return this.item.genres.join(' / ')
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            async handler(value) {
                this.id = value
                this.item = await this.$api.subsonic.getArtistDetails(value)
                this.isFavourite = !!this.item.favourite
                this.showOverviewPanel = false
                this.activeSection = 'tracks'
            }
        }
    },
    methods: {
        playNow() {
            return this.$store.dispatch('playTrackList', {
                tracks: this.item.topTracks
            })
        },
        shuffleNow() {
            return this.$store.dispatch('player/shuffleNow', {
                tracks: this.item.topTracks
            })
        },
        setDisplayMode(isOverview) {
            this.showOverviewPanel = isOverview
        },
        addFavouriteArtist() {
            if (this.isFavourite) {
                this.$api.subsonic.removeFavourite(this.item.id, 'artist').then(() => {
                    this.isFavourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(this.item.id, 'artist').then(() => {
                    this.isFavourite = true
                })
            }
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/artists/most-albums' })
        },
        viewAll(item) {
            this.$router.push({ path: `/artist/tracks/${item.id}`, query: { name: item.name } })
        },
        openArtist(artist) {
            this.$router.push({ path: '/artist/id/' + artist.id })
        },
        openArtistModal() {
            if (!this.item?.description || !this.$refs.artistModal) {
                return
            }
            this.$refs.artistModal.showModal()
        }
    }
}
</script>

<style scoped>
.listener-artist {
    --display-stage-size: min(19rem, 78vw, 34dvh);
    position: relative;
    height: calc(100dvh - 12.25rem);
    box-sizing: border-box;
    padding: 0.85rem 0.9rem 2rem;
    color: #0f172a;
    background:
        radial-gradient(circle at top, rgba(255, 255, 255, 0.92), rgba(244, 247, 251, 0.94) 48%, rgba(241, 245, 249, 0.98) 100%);
    overflow-y: auto;
    overflow-x: hidden;
}

.listener-artist__backdrop {
    position: absolute;
    inset: 0;
    background-position: center top;
    background-size: cover;
    filter: blur(44px);
    opacity: 0.18;
    transform: scale(1.08);
    pointer-events: none;
}

.listener-artist__glow {
    position: absolute;
    top: -4rem;
    left: 50%;
    width: 22rem;
    height: 22rem;
    border-radius: 999px;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
    pointer-events: none;
}

.listener-artist__shell {
    position: relative;
    z-index: 1;
}

.listener-artist__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-artist__heading {
    flex: 1;
    text-align: center;
    min-width: 0;
}

.listener-artist__eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-artist__caption {
    margin-top: 0.12rem;
    font-size: 0.84rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-artist__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.listener-artist__icon-button svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-artist__hero-switch-wrap {
    margin: 1.15rem auto 0;
    width: 100%;
    display: flex;
    justify-content: center;
}

.listener-artist__hero-switch {
    padding: 0.28rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    width: fit-content;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-artist__hero-pill {
    min-width: 4.7rem;
    height: 2.2rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-artist__hero-pill.is-active {
    background: #111827;
    color: #fff;
}

.listener-artist__display-stage {
    height: var(--display-stage-size);
    margin-top: 1.25rem;
    margin-bottom: 1.45rem;
    display: flex;
    align-items: stretch;
}

.listener-artist__art-stage {
    position: relative;
    margin: 0 auto;
    width: var(--display-stage-size);
    height: var(--display-stage-size);
    align-self: center;
}

.listener-artist__overview-stage {
    width: 100%;
    height: 100%;
    display: grid;
    align-content: start;
    gap: 0.9rem;
    overflow-y: auto;
    padding-right: 0.1rem;
}

.listener-artist__vinyl {
    position: absolute;
    right: -1.5rem;
    top: 1.7rem;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, #0f172a 0 10%, #111827 10% 15%, #020617 15% 52%, #111827 52% 60%, #020617 60% 100%);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.22);
}

.listener-artist__art-card {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    padding: 0.8rem;
    border-radius: 2rem;
    background: linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.36));
    backdrop-filter: blur(20px);
    box-shadow: 0 28px 70px rgba(99, 102, 241, 0.18);
}

.listener-artist__art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.45rem;
}

.listener-artist__meta-kicker {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #94a3b8;
}

.listener-artist__title {
    margin-top: 0.5rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #111827;
    word-break: break-word;
}

.listener-artist__summary {
    margin-top: 0.95rem;
    font-size: 0.92rem;
    line-height: 1.75;
    color: #64748b;
}

.listener-artist__summary.is-clickable {
    cursor: pointer;
}

.listener-artist__chips {
    margin-top: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.listener-artist__chip {
    min-height: 2.1rem;
    padding: 0.45rem 0.9rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    color: #334155;
    font-size: 0.82rem;
    font-weight: 700;
    box-shadow: 0 8px 22px rgba(148, 163, 184, 0.1);
}

.listener-artist__actions {
    margin-top: 1.35rem;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.7rem;
}

.listener-artist__play-button,
.listener-artist__action-button {
    min-height: 3.45rem;
    border: 0;
    border-radius: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    font-weight: 700;
}

.listener-artist__play-button {
    background: #111827;
    color: #fff;
    box-shadow: 0 18px 30px rgba(15, 23, 42, 0.24);
}

.listener-artist__action-button {
    background: rgba(255, 255, 255, 0.82);
    color: #0f172a;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-artist__play-button svg,
.listener-artist__action-button svg {
    width: 1.1rem;
    height: 1.1rem;
}

.listener-artist__display-switch {
    margin: 1.15rem auto 0;
    padding: 0.28rem;
    width: 100%;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    display: inline-grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-artist__display-pill {
    min-width: 0;
    height: 2.2rem;
    padding: 0 0.8rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-artist__display-pill.is-active {
    background: #111827;
    color: #fff;
}

.listener-artist__tracks,
.listener-artist__albums,
.listener-artist__similar {
    margin-top: 1.35rem;
}

.listener-artist__panel-header {
    margin-bottom: 1rem;
}

.listener-artist__panel-eyebrow {
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #94a3b8;
}

.listener-artist__panel-title {
    margin-top: 0.3rem;
    font-size: 1.2rem;
    font-weight: 800;
    color: #111827;
}

.listener-artist__overview-card {
    padding: 1rem;
    border-radius: 1.3rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    backdrop-filter: blur(14px);
}

.listener-artist__overview-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.12em;
}

.listener-artist__overview-text {
    margin-top: 0.75rem;
    color: #475569;
    line-height: 1.8;
    white-space: pre-wrap;
    word-break: break-word;
}

.listener-artist__info-list {
    margin-top: 0.75rem;
    display: grid;
    gap: 0.8rem;
}

.listener-artist__info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.94rem;
}

.listener-artist__info-row span {
    color: #94a3b8;
}

.listener-artist__info-row strong {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
}

.listener-artist__more-button {
    width: 100%;
    margin-top: 0.85rem;
    min-height: 2.9rem;
    border: 0;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.88);
    color: #111827;
    font-weight: 700;
}

.listener-artist__album-name,
.listener-artist__album-sub,
.listener-artist__similar-name,
.listener-artist__similar-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-artist__album-name {
    margin-top: 0.7rem;
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
}

.listener-artist__album-sub {
    margin-top: 0.25rem;
    font-size: 0.8rem;
    color: #64748b;
}

.listener-artist__similar-list {
    display: grid;
    gap: 0.8rem;
}

.listener-artist__similar-row {
    border: 0;
    padding: 0.75rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.82);
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: center;
    text-align: left;
}

.listener-artist__similar-image {
    width: 3.4rem;
    height: 3.4rem;
    object-fit: cover;
    border-radius: 1rem;
}

.listener-artist__similar-name {
    font-size: 0.92rem;
    font-weight: 700;
    color: #111827;
}

.listener-artist__similar-sub {
    margin-top: 0.25rem;
    font-size: 0.8rem;
    color: #64748b;
}

.listener-artist__similar-arrow {
    width: 1rem;
    height: 1rem;
    color: #94a3b8;
}

.listener-artist__empty-inline {
    text-align: center;
    color: #64748b;
    padding: 1.5rem 0 0.5rem;
}
</style>
